/**
 * <nia-timer>
 * Temporizador del batido. Cuenta con requestAnimationFrame y tiempos
 * absolutos de performance.now(), no con setInterval: sumar intervalos acumula
 * desfase, y en veinte segundos eso ya se nota.
 *
 * El anillo es decorativo y está oculto al lector de pantalla. El conteo se
 * anuncia en una región viva cada cinco segundos, no en cada tick, que sería
 * insoportable.
 */
class NiaTimer extends HTMLElement {
  #state = 'idle';
  #elapsed = 0;
  #startedAt = 0;
  #frame = null;
  #lastAnnounced = null;
  #lastWholeSecond = null;

  connectedCallback() {
    this.bar = this.querySelector('[data-timer-bar]');
    this.countEl = this.querySelector('[data-timer-count]');
    this.unitEl = this.querySelector('[data-timer-unit]');
    this.button = this.querySelector('[data-timer-toggle]');
    this.note = this.querySelector('[data-timer-note]');
    this.status = this.querySelector('[data-timer-status]');

    this.duration = (Number(this.dataset.duration) || 20) * 1000;
    this.circumference = Number(this.dataset.circumference) || 439.82;
    this.labels = {
      start: this.dataset.startLabel || 'Iniciar',
      stop: this.dataset.stopLabel || 'Detener',
      again: this.dataset.againLabel || 'Otra vez',
      done: this.dataset.doneLabel || 'Listo',
    };
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.button?.addEventListener('click', this.#onToggle);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#stopFrame();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #onToggle = () => {
    if (this.#state === 'running') this.#pause();
    else this.#run();
  };

  #run() {
    if (this.#state === 'done') this.#reset();
    this.#state = 'running';
    this.#startedAt = performance.now() - this.#elapsed;
    this.classList.remove('is-done');
    if (this.note) this.note.hidden = true;
    if (this.button) this.button.textContent = this.labels.stop;
    this.#stopFrame();
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #pause() {
    this.#state = 'paused';
    this.#stopFrame();
    if (this.button) this.button.textContent = this.labels.start;
  }

  #reset() {
    this.#elapsed = 0;
    this.#lastAnnounced = null;
    this.#lastWholeSecond = null;
    this.#paint(0);
  }

  #stopFrame() {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  #tick = (now) => {
    this.#elapsed = Math.min(now - this.#startedAt, this.duration);
    this.#paint(this.#elapsed);

    if (this.#elapsed >= this.duration) {
      this.#finish();
      return;
    }

    this.#frame = requestAnimationFrame(this.#tick);
  };

  /** @param {number} elapsed en milisegundos */
  #paint(elapsed) {
    const remaining = Math.max(0, this.duration - elapsed);
    const seconds = Math.ceil(remaining / 1000);
    const progress = this.duration > 0 ? elapsed / this.duration : 0;

    if (this.countEl) this.countEl.textContent = String(seconds);

    // Con movimiento reducido el anillo avanza a saltos de un segundo.
    if (this.bar) {
      if (this.reduced) {
        if (seconds !== this.#lastWholeSecond) {
          const stepped = (this.duration - seconds * 1000) / this.duration;
          this.bar.setAttribute('stroke-dashoffset', String(this.circumference * (1 - stepped)));
          this.#lastWholeSecond = seconds;
        }
      } else {
        this.bar.setAttribute('stroke-dashoffset', String(this.circumference * (1 - progress)));
      }
    }

    // Cada cinco segundos, no en cada cuadro.
    if (seconds > 0 && seconds % 5 === 0 && seconds !== this.#lastAnnounced) {
      this.#lastAnnounced = seconds;
      if (this.status) this.status.textContent = `Quedan ${seconds} segundos`;
    }
  }

  #finish() {
    this.#state = 'done';
    this.#stopFrame();
    this.classList.add('is-done');

    if (this.bar) this.bar.setAttribute('stroke-dashoffset', '0');
    if (this.countEl) this.countEl.textContent = this.labels.done;
    if (this.button) this.button.textContent = this.labels.again;
    if (this.note) this.note.hidden = false;
    if (this.status) this.status.textContent = `${this.labels.done}. ${this.note?.textContent.trim() ?? ''}`;

    this.#beep();
  }

  /**
   * Una pestaña oculta congela requestAnimationFrame, así que al volver el
   * tiempo transcurrido habría dado un salto. Se pausa y se reanuda sola.
   */
  #onVisibility = () => {
    if (document.hidden && this.#state === 'running') {
      this.#elapsed = performance.now() - this.#startedAt;
      this.#stopFrame();
      this.#state = 'hidden';
      return;
    }

    if (!document.hidden && this.#state === 'hidden') this.#run();
  };

  /** Tono corto generado en el momento: no se descarga ningún archivo. */
  #beep() {
    if (this.dataset.beep !== 'true') return;

    try {
      const Context = window.AudioContext || window.webkitAudioContext;
      if (!Context) return;

      const context = new Context();
      const oscillator = context.createOscillator();
      const gain = context.createGain();

      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, context.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.35);

      oscillator.connect(gain).connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.36);
      oscillator.onended = () => context.close();
    } catch (_) {
      // Sin audio disponible el temporizador funciona igual.
    }
  }
}

if (!customElements.get('nia-timer')) {
  customElements.define('nia-timer', NiaTimer);
}
